import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions, WriteRequestOptions } from '../../requestOptions';
import type { ExpansionCategoryOption, ExpansionRecategorization } from './types';

const validateRun = (runId: number, subscriptionId: number) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
};

/** Any subscription of the organization — the API authorises against it and scopes reads by organization. */
type RunParams = { runId: number; subscriptionId: number };

export type ExpansionCategoriesResponse = { data: ExpansionCategoryOption[] };

/**
 * The destination's leaf categories whose path matches every word of `query` — the "Move to category"
 * picker's type-ahead. Only these are categories a move accepts. `query` is sent only when it says
 * something, so an empty box lists the first categories alphabetically.
 */
export const searchRunCategories = async (
  scope: RequestScope,
  { runId, subscriptionId, query }: RunParams & { query?: string },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<ExpansionCategoriesResponse>> => {
  try {
    validateRun(runId, subscriptionId);
    const q = (query ?? '').trim();

    const result = await scopedChannelApi(scope).get<ExpansionCategoriesResponse>(`atts/expansion/runs/${runId}/categories`, {
      ...options,
      params: q === '' ? {} : { q },
      headers: { 'x-astur-subscription-id': subscriptionId.toString() },
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export type RecategorizeResponse = { recategorization: ExpansionRecategorization };

/**
 * Moves the selected products — the API brings their whole families with them — to `groupingId`,
 * where they are rendered and filled again. 202 with the queued request; 409 with a `code` while the
 * run cannot take a move; 422 for a category or product that is not the run's to move.
 */
export const recategorizeProducts = async (
  scope: RequestScope,
  { runId, subscriptionId, productIds, groupingId, requestedBy }: RunParams & { productIds: number[]; groupingId: number; requestedBy?: string },
  options: WriteRequestOptions
): Promise<ChannelApiResponse<RecategorizeResponse>> => {
  try {
    validateRun(runId, subscriptionId);
    if (productIds.length === 0) {
      throw new Error('Select at least one product to move');
    }
    if (!groupingId || groupingId < 1) {
      throw new Error('Choose a category to move the products to');
    }

    const result = await scopedChannelApi(scope).post<RecategorizeResponse>(
      `atts/expansion/runs/${runId}/recategorize`,
      { product_ids: productIds, grouping_id: groupingId, ...(requestedBy ? { requested_by: requestedBy } : {}) },
      { ...options, headers: { 'x-astur-subscription-id': subscriptionId.toString() } }
    );

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

export type RecategorizationsResponse = { data: ExpansionRecategorization[] };

/** The run's latest moves, newest first — polled while one is pending or running. */
export const listRecategorizations = async (
  scope: RequestScope,
  { runId, subscriptionId }: RunParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<RecategorizationsResponse>> => {
  try {
    validateRun(runId, subscriptionId);

    const result = await scopedChannelApi(scope).get<RecategorizationsResponse>(`atts/expansion/runs/${runId}/recategorizations`, {
      ...options,
      headers: { 'x-astur-subscription-id': subscriptionId.toString() },
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
